import { useState, useEffect } from "react"
import References from "../../helpers/Reference.js";
import { CustomDatepicker } from "../datepicker/custom.datepicker"

import dayjs from 'dayjs';
import { Bar } from 'react-chartjs-2';
import { ExportChartData } from "./export.js";

import { Row, Col, Space, Button } from 'antd';
import { FileExcelOutlined, ReloadOutlined } from '@ant-design/icons';

import { loadAnalytics, parseAnalytics, ShowLocaleDate, GetDecade, FormatDateByDecade, ShowLocaleDateTime, GetSeason } from "../../helpers/helpers.js";
import { ExportAggregateData } from "./export-aggregate.js";
import { AnalyticHelpContext } from "./components/help-context-analytic.js";

const _CURR_DECADE = GetDecade( dayjs().date());
const monthes = dayjs.monthsShort().map(val => val.replace(/\./, ''));;

export const Analytic = ({station, measurer, setLoaded, onHelpContextClick }) => {
   
    const [ start, setStart ] = useState( dayjs(FormatDateByDecade(_CURR_DECADE, dayjs().month(), dayjs().year(), true)) );
    const [ finish, setFinish ] = useState( dayjs(FormatDateByDecade(_CURR_DECADE, dayjs().month(), dayjs().year(), false)) );
    const [ periodAgg, setPeriodAgg ] = useState('D');
    const [ periodType, setPeriodType ] = useState('E');
    const [ isShowExportData, setIsShowExportData ] = useState( false );
    const [ data, setData ] = useState([]);

    useEffect(() => {
        onRefresh( start, finish, periodAgg );
    }, 
    [ measurer ]);

    const onRefresh = ( start, finish, periodAgg ) => {
        setLoaded( false );
        loadAnalytics( measurer.uuid, measurer.type, start, finish, periodAgg )
        .then( response => {
            let data = parseAnalytics( measurer.type, response );            
            setData( data );
            setLoaded( true );
        })
    }

    const onAcceptClick = ( start, finish, periodAgg, periodType ) => {
        setStart( start );
        setFinish( finish );
        setPeriodAgg( periodAgg );
        setPeriodType( periodType );
        onRefresh( start, finish, periodAgg );
    }

    const onShowChartClick = () => {
        onRefresh( start, finish, periodAgg );
    }

    const onAggregateExportClick = () => {
        let currIsShowedExportData = isShowExportData;
        setIsShowExportData( !currIsShowedExportData );
    }

    return  (
    <>
    <Row className="bg-color-eee borderb-555 flex-shrink-0">
    <Col span="24">
        <div className="p-1">
            <Space>
                <CustomDatepicker isCustom={true} start={start} finish={finish} chooseAggregate={periodAgg} choosePeriodType={periodType} isAggregate={true} onAccept={onAcceptClick}/>
                <Button type="default" shape="circle" icon={<ReloadOutlined />} onClick={onShowChartClick} className="no-draggable"/>                                    
                <Button type="default" shape="circle" icon={<FileExcelOutlined />} onClick={onAggregateExportClick} className="no-draggable"/>                                    
            </Space>
        </div>
    </Col>
    </Row>

    <div className="pos-relative flex-grow-1">
        <ChartAggregate dataset={data} agg={periodAgg}/>
    </div>  

    {isShowExportData && 
        <ExportAggregateData 
            start={start} finish={finish} 
            station={station.station} measurer={measurer} 
            aggregate={periodAgg}
            onClose={onAggregateExportClick}
        />
    }    

    </>        
)}

const ChartAggregate = ({dataset, agg}) => {
    if (!dataset.length)
        return null;

    let max = dataset.reduce(function (max, val) {
        if (val.hasOwnProperty('max')) {
            if (max === 'start' || max < val.max)
                max = val.max;
        }
        return max;
    }, 'start');

    let min = dataset.reduce(function (min, val) {
        if (val.hasOwnProperty('min')) {
            if (min === 'start' || min > val.min)
                min = val.min;
        }
        return min;
    }, 'start');
        
    const labels = dataset.map(val => ShowLocaleDate(val?.x));

    const getMaxDataset = () => {
        if (!dataset.length || !dataset[0]?.datasets.includes('high'))
            return [];

        const maxDataset = dataset.map(val => [val?.a, val?.max]);        
        return {                 
            label: References.dictionary.max, 
            backgroundColor: "rgba(121, 200, 121, 0.8)",
            borderColor: "black",
            borderSkipped: false,
            borderWidth: {
            top: 0,
            bottom: 2,
            right: 0,
            left: 0
            },                
            data: maxDataset,
            fill: "-1",
            line: false,
            radius: 0,
        }

    }   
    
    const getMinDataset = () => {
        if (!dataset.length || !dataset[0]?.datasets.includes('low'))
            return [];

        const minDataset = dataset.map(val => [val?.min, val?.a]);
        return {
            label: References.dictionary.min,
            backgroundColor: "rgba(167, 147, 143, 0.8)",
            borderColor: "black",
            borderWidth: 0,
            data: minDataset,
            fill: "-1",
            radius: 0,
          }
    }

    const getAvgDataset = () => {
        if (!dataset.length || !dataset[0]?.datasets.includes('avg'))
            return [];

        const avgDataset = dataset.map(val => [val?.a, val?.a ]);        
        return {
            label: References.dictionary.avg,
            backgroundColor: "rgba(30, 30, 30, 0.8)",
            borderColor: "rgba(100, 100, 100, 0.8)",
            borderWidth: 0,
            data: avgDataset,
            fill: "-1",
            radius: 0,
        }
    }

    const getSumDataset = () => {
        if (!dataset.length || !dataset[0]?.datasets.includes('sum'))
            return [];

        const sumDataset = dataset.map(val => [0, val?.sum]);
        return {
            label: References.dictionary.sum,
            backgroundColor: "rgba(121, 200, 121, 0.8)",
            borderWidth: 0,
            data: sumDataset,
            fill: "-1",
            radius: 0,
        }
    }    

    const options = {
        plugins: {
            legend: {
                display: false
            },
            layout: {
                padding: {
                    bottom: 20
                }
            },
            title: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,  
                callbacks: {
                    title: function( context ) {
                        return getAggregatePeriodTitle( dataset, context[0].dataIndex, agg );
                    },
                    label: function( context ) {
                        const label = context.dataset.label;
                        let value = context.datasetIndex !== 1 ? context.raw[1] : context.raw[0];
                        if (!value)
                            value = '';
                        else
                            value = new Intl.NumberFormat(References.locale).format(value);
                        return `${label}: ${value}`;
                    },
                }    
            },
            hover: {
                mode: 'index',
                intersect: false
            },
        },        
        tooltips: {
          mode: 'index',
          intersect: false,
          displayColors: false,
        },
        interaction: {
            intersect: false,
            mode: 'index',
          },        
        responsive: true,
        maintainAspectRatio: false,        
        scales: {
          x: {
            stacked: true,
          },
          y: {
            stacked: false,
            scaleLabel: {
              display: true,
              labelString: "value"
            },
            min: min - Math.abs(min * 0.2),
            max: max + Math.abs(max * 0.2)
          }
        }
    }
   
    return (   
    <Bar
    data={
        {labels: labels,
        datasets: [
            getMaxDataset(),
            getMinDataset(),
            getAvgDataset(),
            getSumDataset() 
        ]
    }}
    options={options}
/>)
} 

const getAggregatePeriodTitle = ( dataset, index, agg ) => {    
    const time = dayjs(dataset[index]?.x);
    let result = null;
    switch (agg) {
        case '12H':
            result = ShowLocaleDateTime( time );
        break;
        case 'D':
        case 'E':            
            result = ShowLocaleDate( time );
        break;

        case '1M':
            result = `${monthes[time.month()]} ${time.year()}`;
        break;
        case 'A':
            result = `${References.seasons[GetSeason( new Date(time) )]} ${time.year()}`;
        break;
        case 'Y':
            return time.year();
        break;
        default:
            result = null;
    }
    return result;
}